import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { tv } from 'tailwind-variants';
import { cn } from '@/shared/utils';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  ItemsListEmptyState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '.';

type SelectItem = Record<string, unknown> & {
  value: string;
};

type Props<T = SelectItem> = Omit<
  React.ComponentProps<typeof FormField>,
  'render'
> & {
  name: string;
  label: string;
  items: Array<T>;
  renderItem: (item: T) => React.ReactNode;
  renderSelectedValue?: (value: string) => React.ReactNode;
  className?: string;
  contentClassName?: string;
  contentViewportClassName?: string;
  showOptionCheckmark?: boolean;
  onPointerDown?: (value: string) => void;
  disabled?: boolean;
  contentAlign?: 'start' | 'center' | 'end';
  onValueChange?: (value: string) => void;
  getItemKey?: (item: T) => string;
  withChevron?: boolean;
};

const formFieldSelectorStyles = tv({
  slots: {
    trigger:
      'headline4 hover:bg-stroke w-full h-[6rem] rounded-[1.6rem] bg-muted px-[1.6rem] cursor-pointer',
    content: 'rounded-[1.6rem] shadow-sm p-0 overflow-hidden',
    option: 'cursor-pointer px-[2.4rem] py-[1.2rem]',
  },
});

const FormFieldSelectInput = <T extends SelectItem = SelectItem>({
  name,
  label,
  items,
  renderItem,
  renderSelectedValue,
  className,
  contentClassName,
  contentViewportClassName,
  showOptionCheckmark,
  onPointerDown,
  disabled = false,
  contentAlign = 'start',
  onValueChange,
  getItemKey,
  withChevron = false,
}: Props<T>) => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const { trigger, content, option } = formFieldSelectorStyles();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <Select
            onValueChange={value => {
              field.onChange(value);
              onValueChange?.(value);
            }}
            value={field.value}
            disabled={disabled}
            open={undefined}>
            <FormControl>
              <SelectTrigger
                size="md"
                floatingLabel={label}
                withChevron={withChevron}
                value={field.value}
                className={cn(trigger(), className)}>
                {renderSelectedValue ? (
                  renderSelectedValue(field.value)
                ) : (
                  <SelectValue />
                )}
              </SelectTrigger>
            </FormControl>
            <SelectContent
              className={cn(content(), contentClassName)}
              classNameViewport={cn(
                'overflow-y-auto bg-popover',
                contentViewportClassName
              )}
              align={contentAlign}>
              {items.length === 0 && (
                <ItemsListEmptyState
                  title={t('common.noResults')}
                  description={t('common.noResultsDescription')}
                  className="py-[4.8rem] px-[3.2rem]"
                />
              )}
              {items.map(item => (
                <SelectItem
                  key={getItemKey ? getItemKey(item) : item.value}
                  value={item.value}
                  className={option()}
                  showOptionCheckmark={showOptionCheckmark}
                  onPointerDown={() => onPointerDown?.(item.value)}>
                  {renderItem(item)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export { FormFieldSelectInput };
